import { useState, useMemo, useEffect } from 'react';
import { dish } from './i18n.js';
import { track } from './track.js';
import * as G from './games.js';
import { priceText } from './ui.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * THE ELEATER'S GAME DAY — THE GAME FIRST, THEN THE MENU AROUND IT.
 *
 * ⚠️⚠️⚠️ THIS IS `Eleater Prototype/11_Restaurant.html`'s Game Day pane,
 * rebuilt. Not designed here. Every control below is in that file, in this
 * order, with Al's own instructions quoted beside it, and React had NONE of
 * them — it drew the Game Day deals as a flat list with no game, no kick-off,
 * no day and no phase.
 *
 * Al, 25 September 2026: *"This was supposed to be the whole fucking entire
 * game plan, pun intended, for the games to be shown and what food… Arsenal
 * versus Liverpool, 11 p.m., half price wings."*
 *
 * THE ORDER, FROM THE LOCKED FILE:
 *   .sportstrip   → sport is the FIRST choice, only sports with a game
 *   .weekhead     → ‹ This week · September › , week by week
 *   .dowd         → day + DATE chips (never a bare weekday)
 *   .timechip     → league · when · phase
 *   .gdwindow     → "Game Day menu runs 6:00 PM – 12:00 AM"
 *   .dow #gdFix   → the games on that day, sport first on the chip
 *   .dow #gdPhase → All-day · Before game · During game · After game
 *   .msub         → Food · Combos · Alcoholic · Non-Alcoholic
 *   .gdnoday      → a day with no game has NO Game Day menu
 *
 * ⚠️ NO NEW COLOURS AND NO NEW CLASSES. Every className here already exists
 * in `eleat.css`, which carries the locked screen's own rules (RULE ZERO-A).
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE FOUR PHASES ARE 019's OWN CHECK CONSTRAINT —
   `all_day | before | during | after` on both `game_day_menu_items.phase`
   and `game_day_game_items.phase`. The labels are the locked screen's.
   The key is the schema's; the label is what an Eleater reads; they are
   listed together here once so they can never drift apart. */
const PHASES = [
  { id: 'all_day', label: 'All-day', meta: 'All Day' },
  { id: 'before', label: 'Before game', meta: 'Before Game' },
  { id: 'during', label: 'During game', meta: 'During Game' },
  { id: 'after', label: 'After game', meta: 'After Game' },
];

/* ⚠️ THE CATEGORY KEYS ARE THE LOCKED `deal_section` ENUM (001_schema.sql) —
   `alcoholic · food · combos · non_alcoholic`. The locked screen's own chip
   order is Food, Combos, Alcoholic Drinks, Non-Alcoholic, which is NOT the
   enum's resolution order, and that is deliberate on that screen. */
const CATS = [
  { id: 'food', label: 'Food' },
  { id: 'combos', label: 'Combos' },
  { id: 'alcoholic', label: 'Alcoholic Drinks' },
  { id: 'non_alcoholic', label: 'Non-Alcoholic' },
];

const ALL_DAY = '__allday__';

export default function GameDayPane({ data, tz, clockFormat = '12h', venueId = null }) {
  /* ⚠️ THE VENUE'S CLOCK — set before anything below reads a time. See
     `setZone` in games.js: the card and this pane must agree on a kick-off. */
  G.setZone(tz);
  G.setClock(clockFormat);   /* 151 — the venue's 12h / 24h */
  /* ⚠️ "WE COULD NOT ASK" AND "THERE IS NOTHING ON" ARE DIFFERENT SENTENCES.
     Same rule the deal tabs follow — undefined is still-asking. */
  const [sport, setSport] = useState('all');
  const [weekOff, setWeek] = useState(0);
  const [game, setGame] = useState(null);
  const [phase, setPhase] = useState('all_day');
  const [cat, setCat] = useState('food');

  const menu = data && data.menu;
  const games = useMemo(
    () => ((data && data.games) || []).filter(G.isLive),
    [data],
  );
  /* the day the strip opens on — `gdLandOn()`: a day with a game */
  const [day, setDay] = useState(() => G.landOn(games, 0));

  /* ── the sports strip ───────────────────────────────────────────────────
     ⚠️ ONLY THE SPORTS THIS VENUE HAS A GAME IN. A tab for a sport with
     nothing behind it is a promise Eleat cannot keep. */
  const sports = useMemo(() => G.sportsIn(games), [games]);

  /* the games matching the sport filter — everything downstream reads this */
  const inSport = useMemo(
    () => games.filter((g) => sport === 'all' || g.sport === sport),
    [games, sport],
  );

  const days = useMemo(() => G.week(inSport, weekOff), [inSport, weekOff]);
  const ahead = useMemo(() => G.weeksAhead(inSport), [inSport]);
  const behind = useMemo(() => G.weeksBehind(inSport), [inSport]);

  const onDay = useMemo(
    () => G.gamesOn(inSport, day, sport),
    [inSport, day, sport],
  );

  /* ⚠️ THE CHOSEN GAME MUST BE ONE OF THIS DAY'S, or the menu underneath
     belongs to a different day than the one selected. The locked screen
     re-points it rather than showing a mismatch, and so does this. */
  const chosen = useMemo(() => {
    if (game === ALL_DAY) return null;
    const found = onDay.find((g) => g.id === game);
    return found || onDay[0] || null;
  }, [onDay, game]);

  const allDayOnly = game === ALL_DAY;

  /* ⚠️ ANALYTICS — 3 Oct 2026. Game Day's games are not deal rows, so the
     Game Day page counts these: the venue's Game Day seen, by game, and a
     dish opened from it. */
  const seenGame = chosen ? G.title(chosen) : null;
  useEffect(() => {
    if (venueId) track('game_day_viewed', { restaurant_id: venueId, source_screen: 'venue',
      metadata: seenGame ? { game: seenGame } : {} });
  }, [venueId, seenGame]);

  /* ── the offers ─────────────────────────────────────────────────────────
     ⚠️ A GAME WITH `custom_menu` CARRIES ITS OWN COPY — 019: *"Rare override
     for marquee occasions."*  The shared venue menu is the ordinary case and
     the default. */
  const items = useMemo(() => {
    if (chosen && chosen.custom_menu) {
      const mine = ((data && data.overrides) || []).filter((o) => o.game_id === chosen.id);
      if (mine.length) return mine;
    }
    return (data && data.items) || [];
  }, [data, chosen]);

  /* ⚠️ A CATEGORY CHIP WITH NOTHING BEHIND IT IS NOT DRAWN — the same rule as
     the sports strip, and the locked screen does exactly this:
     `gdCats.filter(c => gdData.some(x => x.cat === c.id))`. */
  const cats = useMemo(
    () => CATS.filter((c) => items.some((it) => it.section === c.id)),
    [items],
  );

  /* ⚠️ IN `all_day` MODE THERE IS NO PHASE ROW AT ALL. 019:
     *"all_day = a single set of specials live from before-lead to after-trail
     (the DEFAULT — most bars want only this) | phased = separate Before /
     During / After sets."*  Drawing three empty phase chips for a venue that
     never phased its menu is the "tab with nothing behind it" fault again. */
  const phased = !!(menu && menu.mode === 'phased');
  const phases = useMemo(
    () => (phased ? PHASES.filter((p) => p.id === 'all_day'
      || items.some((it) => it.phase === p.id)) : []),
    [phased, items],
  );

  const rows = useMemo(() => items.filter((it) => {
    if (it.section !== cat) return false;
    if (allDayOnly) return it.phase === 'all_day';
    if (!phased) return true;
    if (phase === 'all_day') return true;
    return it.phase === phase || it.phase === 'all_day';
  }), [items, cat, phase, phased, allDayOnly]);

  /* ⚠️ THE VENUE-LEVEL SWITCH IS OFF. 019: *"Off = no Game Day offers show to
     Eleaters at all, regardless of games."*  The server already returns empty
     lists in that case; this says which of the two silences it is. */
  if (!menu || !menu.is_live) {
    return <p className="mut">No Game Day at the moment.</p>;
  }

  if (!games.length) {
    return <p className="mut">No games listed at the moment.</p>;
  }

  const win = chosen ? G.windowOf(chosen, menu) : null;
  const phaseMeta = (PHASES.find((p) => p.id === phase) || PHASES[0]).meta;

  return (
    <div className="gdpane">

      {/* ══ SPORT IS THE FIRST CHOICE ═════════════════════════════════════
          Al, 15 September 2026: *"You see, you don't have the multiple sports
          categories which is already in the restaurant admin UI."* and *"MMA
          could be in the same evening as a Monday night football game or a
          soccer game or a golf match."*  Drawn only when there is more than
          one sport — a single-sport venue does not need to be asked. */}
      {sports.length > 1 && (
        <div className="sportstrip">
          {/* ⚠️ `.on`, NOT `.active` — the locked strip's own state class,
              quoted from `view_all_gameday.html` where it has lived since
              13 September. The day chips below use `.active`. Two rows, two
              contracts, and mixing them silently draws an unlit chip. */}
          <button type="button" className={sport === 'all' ? 'on' : ''}
            onClick={() => { setSport('all'); setGame(null); setDay(G.landOn(games, weekOff)); }}>All sports</button>
          {sports.map((s) => (
            <button key={s} type="button" className={sport === s ? 'on' : ''}
              onClick={() => {
                setSport(s); setGame(null);
                setDay(G.landOn(games.filter((g) => g.sport === s), weekOff));
              }}>{s}</button>
          ))}
        </div>
      )}

      {/* ══ THE WEEK ══════════════════════════════════════════════════════
          ⚠️ AND A WEEK IS NOT ENOUGH — Restaurant Admin lets a venue plan 30
          days ahead, so a one-week strip throws away three weeks the venue
          has already entered. The arrows stop where the games stop. */}
      <div className="weekhead">
        <button type="button" aria-label="Earlier"
          disabled={weekOff <= behind}
          onClick={() => { setWeek(weekOff - 1); setGame(null); setDay(G.landOn(inSport, weekOff - 1)); }}>‹</button>
        <span className="wlbl">{G.weekLabel(weekOff)}</span>
        <button type="button" aria-label="Later"
          disabled={weekOff >= ahead}
          onClick={() => { setWeek(weekOff + 1); setGame(null); setDay(G.landOn(inSport, weekOff + 1)); }}>›</button>
      </div>

      {/* ══ THE DAY, WITH ITS DATE ════════════════════════════════════════
          ⚠️ `.dowd` = DAY + DATE, and it must NOT reuse `.dow`. Happy Hour
          and Daily Deals repeat every week for ever, so their chips carry a
          day NAME and no date. A game is on ONE Saturday. */}
      {/* ⚠️ `.d` AND `.n`, `.active`, `.quiet`, `.today` — THE LOCKED
          SCREEN'S OWN CLASS NAMES, already in `eleat.css`. I first wrote
          `.dwd`/`.dwn`/`.dwc`, which match no rule in this app and would
          have rendered a bare unstyled column. ⚠️ AND NO COUNT BADGE:
          the locked `.dowd` chip carries the day and the number and
          nothing else, and a number nobody agreed to is invented copy.

          ⚠️⚠️ AND THE COMMENT SITS HERE, NOT INSIDE THE map(). It was the
          first thing inside `(d) => (`, where JSX has not begun, so `{` was
          read as an object literal and THE WHOLE APP STOPPED BUILDING —
          "Expected ")" but found "key"". Every deploy since has served the
          bundle from before it. A JSX comment is only a comment BETWEEN JSX
          children; anywhere else it is an object. */}
      <div className="dowd">
        {days.map((d) => (
          <button key={d.key} type="button"
            className={(d.key === day ? 'active' : '')
              + (d.isPast ? ' quiet' : '') + (d.isToday ? ' today' : '')}
            onClick={() => { setDay(d.key); setGame(null); }}>
            <span className="d">{d.short}</span>
            <span className="n">{d.num}</span>
          </button>
        ))}
      </div>

      {/* ══ A DAY WITH NO GAME HAS NO GAME DAY MENU ═══════════════════════
          Al, 15 September 2026: *"on the no games, you've got specials,
          combos, alcoholic, nonalcoholic drinks, and everything else there.
          Why?"*  The menu is computed from a game's own kick-off — no game,
          no window, no menu. ⚠️ AND IT RETURNS HERE rather than hiding the
          rows below: a hidden list still holds its rows, and anything reading
          the rendered screen would still find a during-the-match special for
          a game that does not exist. */}
      {!onDay.length ? (
        <div className="gdnoday">
          <b>No games on {G.dayLong(day)}</b>
          The Game Day menu runs around a game. Pick another day, or see the
          Signature Menu and Happy Hour.
        </div>
      ) : (
        <>
          {/* ══ WHICH GAME ═════════════════════════════════════════════════
              ⚠️ THE SPORT IS THE FIRST WORD ON THE CHIP, so a day with
              football, a cup tie and a race weekend on it can be scanned
              rather than read. */}
          <div className="dow gdfix">
            {onDay.map((g) => (
              <button key={g.id} type="button"
                className={'mcat' + (chosen && g.id === chosen.id && !allDayOnly ? ' active' : '')}
                onClick={() => setGame(g.id)}>
                {g.sport ? g.sport + ' · ' : ''}{G.title(g)} · {G.f12(G.localMinutes(g.starts_at))}
                {/* ⚠️ A POSTPONED GAME SAYS SO RATHER THAN VANISHING. A game
                    that disappears from a bar's list the morning it is called
                    off is how somebody turns up for nothing. */}
                {g.is_postponed && <span className="gtag"> Postponed</span>}
              </button>
            ))}
            {/* ⚠️ ALL-DAY IS NOT A GAME and keeps its own label. Offered only
                when the venue actually has all-day specials, or it is a chip
                that filters to nothing. */}
            {items.some((it) => it.phase === 'all_day') && (
              <button type="button"
                className={'mcat' + (allDayOnly ? ' active' : '')}
                onClick={() => setGame(ALL_DAY)}>All-day</button>
            )}
          </div>

          {/* ══ THE HEADER SAYS WHEN THE MENU RUNS ═══════════════════════
              ⚠️ NOT JUST WHEN THE GAME STARTS. Restaurant Admin prints
              "menu live 6:00 PM – 12:00 AM" under every game; an Eleater
              looking at the same game deserves the same fact, worked out by
              the same function so the two can never disagree. */}
          {allDayOnly ? (
            <div className="timechip">All-Day Specials · {phaseMeta}</div>
          ) : chosen && (
            <>
              <div className="timechip">
                {chosen.league_short || chosen.league || chosen.sport}
                {' · '}{G.whenText(chosen)}
                {phased ? ' · ' + phaseMeta : ''}
              </div>
              {win && (
                <div className="gdwindow">
                  Game Day menu runs {win.fromText} – {win.toText}
                </div>
              )}
            </>
          )}

          {/* ══ THE PHASE ════════════════════════════════════════════════
              Only in `phased` mode — see the note above `phases`. */}
          {phases.length > 1 && (
            <div className="dow">
              {phases.map((p) => (
                <button key={p.id} type="button"
                  className={'mcat' + (phase === p.id ? ' active' : '')}
                  onClick={() => setPhase(p.id)}>{p.label}</button>
              ))}
            </div>
          )}

          {/* ══ THE CATEGORY ═════════════════════════════════════════════ */}
          {cats.length > 1 && (
            <div className="msub">
              {cats.map((c) => (
                <button key={c.id} type="button"
                  className={'mcat' + (cat === c.id ? ' active' : '')}
                  onClick={() => setCat(c.id)}>{c.label}</button>
              ))}
            </div>
          )}

          {/* ══ THE OFFERS ═══════════════════════════════════════════════
              ⚠️ THE ROW SAYS WHAT IT IS — `data-section` and `data-alcohol`
              on the row itself, not on the list wrapper. Until 11 September
              they lived on the wrapper, so an item lifted out of its list
              (which is exactly what the search index does) arrived with no
              idea whether it was food or a drink.

              ⚠️ AND THE GAME CAN BE ON WITH NO SPECIALS BEHIND IT. That is a
              real state and it still shows the game — which is the entire
              point of this pane. */}
          {!rows.length ? (
            <div className="gempty">
              No specials for this filter — try another category
              {phases.length > 1 ? ' or phase' : ''}.
            </div>
          ) : rows.map((it) => (
            <div key={it.id || it.menu_item_id} className={it.sold_out ? 'hrow soldout' : 'hrow'}
              role="button" tabIndex={0} aria-disabled={it.sold_out || undefined}
              onClick={() => { if (venueId) track('game_day_opened', { restaurant_id: venueId, source_screen: 'venue',
                metadata: { item: it.name, ...(seenGame ? { game: seenGame } : {}) } });
                window.dispatchEvent(new CustomEvent('eleat:item', { detail: {
                name: dish(it).name, description: dish(it).description, original: dish(it).original, price: it.price,
                image_url: it.image_url, offer: it.deal_text, sold_out: it.sold_out === true } })); }}
              data-section={it.section}
              data-alcohol={it.section === 'alcoholic' ? 1 : 0}>
              <div className="l">
                <div className="hname">{dish(it).name}</div>
                {dish(it).description && <div className="hdesc">{dish(it).description}</div>}
                {/* ⚠️ `deal_text` IS THE OFFER WORDING, NOT A SECOND PRICE.
                    019 gives the examples: "Pitcher $128", "$5 first pint",
                    "2 for $99". Two copies of one price is how the address
                    ended up shorter on screen than in its own link. */}
                {it.deal_text && <div className="gtags"><span className="gtag">{it.deal_text}</span></div>}
              </div>
              {/* ⚠️ A NULL PRICE PRINTS NOTHING, NEVER A ZERO. Same rule as
                  the review sub-scores: an unanswered number is not 0. */}
              {(it.price != null || it.sold_out) && (
                <div className="hprice">
                  {it.price != null && priceText(it.price)}
                  {/* ran out tonight (114) — greyed, back tomorrow */}
                  {it.sold_out && <div className="soldtag">Sold out</div>}
                </div>
              )}
              {/* the same photo slot as every other menu — first, and
                  invisible when the restaurant has added no photograph */}
              {it.image_url
                ? <div className="mthumb"><img src={it.image_url} alt="" loading="lazy"
                    onError={(e) => { e.currentTarget.closest('.mthumb').classList.add('hold');
                                      e.currentTarget.remove(); }} /></div>
                : <div className="mthumb hold" />}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
